/**
 * Helpers for the Data Collection "Generate … with AI needs a model
 * provider" warning: whether the commercial-model catalog has no configured
 * provider, whether a details form's selections need AI generation, and the
 * warning-modal copy shown on Next when no provider is configured.
 */

import {
    NO_LABELS_MODE_DEFAULT,
    NO_LABELS_MODE_GENERATE_WITH_AI,
    hasLabelsNouns,
} from '@/features/datasets/utils/hasLabelsHandling';
import { isEmptyGenerateWithAi } from '@/features/datasets/utils/emptyResponsesHandling';

/**
 * True once the provider catalog has loaded with no configured provider.
 *
 * Mirrors the check used by the other Data Collection AI surfaces
 * (GenerateDataWithAIModal / RetrievalAgentModal): while the catalog is
 * still loading we assume providers exist so the warning never flashes on
 * first paint.
 *
 * @param {Object} availableProviders - `GET /available-models` map from
 *   `useCommercialModels` (keyed by provider name).
 * @param {boolean} isLoading - Catalog fetch still in flight.
 * @returns {boolean}
 */
export function hasNoConfiguredProviders(availableProviders, isLoading) {
    return !isLoading && Object.keys(availableProviders || {}).length === 0;
}

/**
 * Whether a details form's current selections need AI generation to run
 * after upload — the conditions the Next-time no-provider warning gates on:
 *
 * - has-labels switch OFF with the "Generate … with AI" mode selected
 *   (an unset mode counts: opting out defaults to Generate-with-AI), or
 * - has-labels ON with a response column selected and the empty-response
 *   handling set to Generate-with-AI. The column guard keeps a stale value
 *   on a hidden empty-responses section from triggering the warning.
 *
 * @param {Object} [details] - One details form (per target or per file).
 * @returns {boolean}
 */
export function detailsWantAiGeneration(details) {
    if (!details) return false;
    if (details.hasLabels === false) {
        return (
            (details.noLabelsMode || NO_LABELS_MODE_DEFAULT) === NO_LABELS_MODE_GENERATE_WITH_AI
        );
    }
    return Boolean(details.labelColumn) && isEmptyGenerateWithAi(details.emptyMultilabelHandling);
}

/**
 * Copy for the Next-time warning shown when a Generate-with-AI selection
 * needs a model provider but none is configured. Task-aware nouns match the
 * radio titles ("Generate labels / entities / summaries / outputs with AI").
 *
 * @param {string} [taskType]
 * @returns {{ title: string, body: string }}
 */
export function noProviderWarningCopy(taskType) {
    const { plural } = hasLabelsNouns(taskType);
    return {
        title: 'No model provider is configured',
        body: `Generating ${plural} with AI requires a model provider. Add an API key or cloud credentials in Account Settings to enable it.`,
    };
}
